import { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useQuery } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { GuestRatingHostModal } from './GuestRatingHostModal';
import { HostRatingGuestModal } from './HostRatingGuestModal';
import { useRatingMutations, CAN_RATE_BOOKING } from './useRatingMutations';

const RateButtonContainer = styled.div`
  ${tw`flex justify-center items-center w-full`}
`;

const RateButton = styled.button`
  ${tw`
    flex
    items-center
    justify-center
    gap-2
    flex-1
    px-5
    py-3
    bg-transparent
    border-2
    border-solid
    border-red-500
    text-red-500
    text-xs
    font-semibold
    rounded-md
    outline-none
    cursor-pointer
    transition-all
    duration-200
    ease-in-out
  `}
  &:hover {
    ${tw`bg-red-500 text-white border-transparent`}
  }
`;

const RatedBadge = styled.div`
  ${tw`flex items-center justify-center gap-2 w-full px-4 py-3 bg-green-100 text-green-800 font-medium rounded-md border-2 border-black`}
`;

const DeadlineText = styled.span`
  ${tw`text-xs text-text-secondary ml-2`}
`;

interface CategoryRating {
  score: number;
  improve?: string[];
}

interface GuestRatingInput {
  general: CategoryRating;
  maintenance: CategoryRating;
  cleanliness: CategoryRating;
  communication: CategoryRating;
  convenience: CategoryRating;
  accuracy: CategoryRating;
  comment?: string;
}

interface RateTripButtonProps {
  bookingId: string;
  isHost: boolean;
  // Host/Guest info for display in modal
  hostName: string;
  hostAvatar?: string;
  guestName: string;
  guestAvatar?: string;
  carName: string;
  tripDates: string;
  // Callback after successful rating
  onRatingSubmitted?: () => void;
  // Whether the booking has already been rated
  alreadyRated?: boolean;
}

export const RateTripButton = ({
  bookingId,
  isHost,
  hostName,
  hostAvatar,
  guestName,
  guestAvatar,
  carName,
  tripDates,
  onRatingSubmitted,
  alreadyRated = false,
}: RateTripButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasJustRated, setHasJustRated] = useState(false);

  const { data, loading, error } = useQuery(CAN_RATE_BOOKING, {
    variables: { bookingId },
    skip: !bookingId || alreadyRated || hasJustRated,
    fetchPolicy: 'network-only',
  });

  const { submitGuestRating, submitHostRating, isSubmittingGuestRating, isSubmittingHostRating } =
    useRatingMutations();

  // Show already rated state
  if (alreadyRated || hasJustRated) {
    return (
      <RatedBadge>
        <FontAwesomeIcon icon={faStar} />
        Rated
      </RatedBadge>
    );
  }

  // Don't show anything while loading or on error
  if (loading || error) return null;

  const eligibility = data?.canRateBooking;
  if (!eligibility) return null;

  // Check if current user can rate
  const canRate = isHost ? eligibility.canHostRate : eligibility.canGuestRate;
  if (!canRate) return null;

  // Calculate days remaining
  const deadline = isHost ? eligibility.hostRatingDeadline : eligibility.guestRatingDeadline;
  const daysRemaining = deadline
    ? Math.ceil((new Date(deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  const handleGuestRatingSubmit = (rating: GuestRatingInput) => {
    submitGuestRating(bookingId, rating, () => {
      setHasJustRated(true);
      setIsModalOpen(false);
      onRatingSubmitted?.();
    });
  };

  const handleHostRatingSubmit = (rating: { general: number; comment?: string }) => {
    submitHostRating(bookingId, rating, () => {
      setHasJustRated(true);
      setIsModalOpen(false);
      onRatingSubmitted?.();
    });
  };

  return (
    <>
      <RateButtonContainer>
        <RateButton onClick={() => setIsModalOpen(true)}>
          <FontAwesomeIcon icon={faStar} />
          {isHost ? 'Rate Guest' : 'Rate Trip'}
          {daysRemaining !== null && daysRemaining <= 3 && (
            <DeadlineText>({daysRemaining} days left)</DeadlineText>
          )}
        </RateButton>
      </RateButtonContainer>

      {/* Guest rating host modal */}
      {!isHost && (
        <GuestRatingHostModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleGuestRatingSubmit}
          hostName={hostName}
          hostAvatar={hostAvatar}
          carName={carName}
          tripDates={tripDates}
          isSubmitting={isSubmittingGuestRating}
        />
      )}

      {/* Host rating guest modal */}
      {isHost && (
        <HostRatingGuestModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleHostRatingSubmit}
          guestName={guestName}
          guestAvatar={guestAvatar}
          carName={carName}
          tripDates={tripDates}
          isSubmitting={isSubmittingHostRating}
        />
      )}
    </>
  );
};
